import { BUGUN, DONEM } from "../config.js";
import { B, stu, tumKiracilar } from "./context.js";
import { donemEkle, donemKatsayisi } from "../lib/fmt.js";
import { feeFor } from "./fees.js";
import { seedHash } from "../lib/avatar.js";
import { state } from "../state.js";

// ============================================================
// FATURA & TAHSİLAT
// Fatura kaydı tutar taşımaz; tutar sınıf tarifesinden hesaplanır.
// Tahsilat hareketleri (transactions) faturanın altında durur,
// durum da bu hareketlerden türetilir.
// ============================================================
export function faturaTutari(student, inv) {
  if (inv.override != null) return inv.override;
  const taban = feeFor(student).computed;
  const k = donemKatsayisi(inv.period);
  return k === 1 ? taban : Math.round((taban * k) / 50) * 50;
}

export const tahsilEdilen = inv => (inv.transactions ?? []).reduce((a, h) => a + h.amount, 0);

export function faturaDurumu(student, inv) {
  const odenen = tahsilEdilen(inv);
  if (odenen >= faturaTutari(student, inv)) return "odendi";
  if (odenen > 0) return "kismi";
  return inv.due < BUGUN ? "gecikti" : "bekliyor";
}

// Bir öğrencinin tüm faturaları, yeniden eskiye.
export const faturalarOf = studentId =>
  B().payments.filter(p => p.studentId === studentId).sort((a, b) => b.period.localeCompare(a.period));

export const payOf = studentId => B().payments.find(p => p.studentId === studentId && p.period === DONEM);

// Ödemeler ekranı: içinde bulunulan dönem + kapanmamış eski faturalar.
export const acikFaturalar = t =>
  t.payments.filter(p => p.period === DONEM || faturaDurumu(stu(p.studentId), p) !== "odendi");

export const totalDue = list => list.reduce((a, p) => a + faturaTutari(stu(p.studentId), p), 0);
export const totalPaid = list => list.reduce((a, p) => a + tahsilEdilen(p), 0);

// Öğrencinin tüm dönemleri: toplam tahakkuk, tahsilat ve kalan bakiye.
export function hesapOzeti(student) {
  const faturalar = faturalarOf(student.id);
  const tahakkuk = faturalar.reduce((a, i) => a + faturaTutari(student, i), 0);
  const tahsil = faturalar.reduce((a, i) => a + tahsilEdilen(i), 0);
  return { faturalar, tahakkuk, tahsil, kalan: tahakkuk - tahsil };
}

export const ODEME_YOLU = ["Havale/EFT", "Kredi kartı", "Nakit", "Otomatik ödeme talimatı"];
export const makbuzNo = (period, h) => `MK-${period.slice(0, 4)}-${String((h % 9000) + 1000)}`;

// feeFor/faturaTutari aktif şubeye göre çalışır; açılışta tüm şubeleri
// gezerken aktif şubeyi geçici olarak değiştiririz.
export function herSubedeCalistir(isle) {
  const oncekiKiraci = state.tenantId;
  const oncekiSube = state.branchId;

  // Hem kiracıyı hem şubeyi ayarlamak zorunlu: feeFor → cls() aktif
  // şubeden okur ve B() araması kiracıyla sınırlıdır. Yalnızca şubeyi
  // değiştirmek, başka kiracının şubesinde yanlış tarifeye düşürür.
  for (const kiraci of tumKiracilar()) {
    state.tenantId = kiraci.id;
    for (const sube of kiraci.branches) {
      state.branchId = sube.id;
      isle(sube);
    }
  }

  state.tenantId = oncekiKiraci;
  state.branchId = oncekiSube;
}

// Elle yazılan faturalara tahsilat hareketi ekler.
export function tahsilatlariKur() {
  herSubedeCalistir(t => {
    t.payments.forEach(inv => {
      if (inv.transactions) return;
      const s = t.students.find(x => x.id === inv.studentId);
      const h = seedHash(inv.studentId + inv.period);
      inv.transactions = !inv.paidOn ? [] : [{
        date: inv.paidOn,
        amount: inv.partial ?? faturaTutari(s, inv),
        method: inv.method ?? "Havale/EFT",
        receiptNo: makbuzNo(inv.period, h),
      }];
    });
  });
}

// Kayıt tarihinden bu yana elle yazılmamış her dönem için fatura üretir.
// Geçmiş dönemler kapanmış sayılır; bir kısmı gecikmeli veya iki taksitte ödenmiştir.
export function faturaGecmisiUret() {
  herSubedeCalistir(t => {
    const yazili = new Set(t.payments.map(p => `${p.studentId}|${p.period}`));
    const uretilen = [];

    t.students.forEach(s => {
      for (let p = s.enrolledAt.slice(0, 7); p < DONEM; p = donemEkle(p, 1)) {
        if (yazili.has(`${s.id}|${p}`)) continue;

        const h = seedHash(s.id + p);
        const due = `${p}-05`;
        const inv = { studentId: s.id, period: p, due, transactions: [] };
        const tutar = faturaTutari(s, inv);

        const gecikmeli = h % 11 === 0;
        const taksitli = h % 17 === 0;
        const gun = gecikmeli ? 8 + (h % 6) : 1 + (h % 4);
        const yol = ODEME_YOLU[(h >>> 5) % ODEME_YOLU.length];

        if (taksitli) {
          const ilk = Math.round(tutar * 0.6 / 50) * 50;
          inv.transactions.push(
            { date: `${p}-0${1 + (h % 3)}`, amount: ilk, method: yol, receiptNo: makbuzNo(p, h) },
            { date: `${p}-${String(15 + (h % 8)).padStart(2, "0")}`, amount: tutar - ilk, method: "Nakit", receiptNo: makbuzNo(p, h >>> 3) },
          );
          inv.note = "İki taksitte tahsil edildi";
        } else {
          inv.transactions.push({
            date: `${p}-${String(gun).padStart(2, "0")}`,
            amount: tutar,
            method: yol,
            receiptNo: makbuzNo(p, h),
          });
          if (gecikmeli) inv.note = "Vade sonrası tahsil edildi";
        }
        uretilen.push(inv);
      }
    });

    t.payments.push(...uretilen);
  });
}
